(() => {
  const COMPONENT_NAME = "mini-product-tile";

  if (customElements.get(COMPONENT_NAME)) return;

  class MiniProductTile extends HTMLElement {
    #abortController = null;
    #toggle = null;
    #details = null;
    #dialog = null;
    #panel = null;
    #closeButton = null;
    #isClosing = false;
    #closeAnimationMs = 450;

    connectedCallback() {
      this.#abortController?.abort();
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.#toggle = this.querySelector(".mini-product-tile__toggle");
      this.#details = this.querySelector(".mini-product-tile__details");
      this.#dialog = this.querySelector("[data-mini-product-tile-dialog]");
      this.#panel = this.querySelector(".mini-product-tile__panel");
      this.#closeButton = this.querySelector("[data-mini-product-tile-close]");

      this.#toggle?.addEventListener("click", this.#handleToggle, { signal });
      this.#closeButton?.addEventListener("click", this.#handleClose, {
        signal,
      });
      this.#dialog?.addEventListener("click", this.#handleDialogClick, {
        signal,
      });
      this.#dialog?.addEventListener("cancel", this.#handleDialogCancel, {
        signal,
      });
      this.#dialog?.addEventListener("close", this.#handleDialogClose, {
        signal,
      });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      document.documentElement.classList.remove(
        "mini-product-tile-drawer-open",
      );
    }

    #setSingleOpen(isOpen) {
      this.classList.toggle("mini-product-tile--open", isOpen);
      this.#toggle?.setAttribute("aria-expanded", String(isOpen));
      this.#details?.setAttribute("aria-hidden", String(!isOpen));
    }

    #openDrawer() {
      if (!this.#dialog || this.#dialog.open || this.#isClosing) return;

      this.#dialog.classList.remove("mini-product-tile__dialog--active");
      this.#dialog.showModal();
      this.#toggle?.setAttribute("aria-expanded", "true");
      document.documentElement.classList.add("mini-product-tile-drawer-open");

      // The panel starts translated fully off-screen, so focusing inside it
      // scrolls the dialog's scroll box across even with preventScroll, which
      // reads as the drawer overshooting and settling back as it slides in.
      this.#closeButton?.focus({ preventScroll: true });
      this.#dialog.scrollLeft = 0;

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.#dialog.classList.add("mini-product-tile__dialog--active");
        return;
      }

      // Two frames: the slide starts on the first frame the browser actually
      // paints after showModal(). WebKit re-resolves the whole document behind
      // the top layer promotion before it paints again, and a transition
      // started before that paint runs to completion unseen. See the colour
      // drawer for the recording that showed it.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.#dialog?.open || this.#isClosing) return;

          this.#dialog.classList.add("mini-product-tile__dialog--active");
        });
      });
    }

    #closeDrawer() {
      if (!this.#dialog?.open || this.#isClosing) return;

      this.#isClosing = true;
      this.#dialog.classList.remove("mini-product-tile__dialog--active");

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.#finishDrawerClose();
        return;
      }

      const timeoutId = window.setTimeout(
        () => this.#finishDrawerClose(),
        this.#closeAnimationMs + 50,
      );

      this.#panel?.addEventListener(
        "transitionend",
        (event) => {
          if (event.target !== this.#panel) return;
          if (event.propertyName !== "transform") return;

          window.clearTimeout(timeoutId);
          this.#finishDrawerClose();
        },
        { once: true },
      );
    }

    #finishDrawerClose() {
      if (this.#dialog?.open) this.#dialog.close();
      this.#isClosing = false;
    }

    #handleToggle = (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (this.#dialog) {
        this.#openDrawer();
        return;
      }

      this.#setSingleOpen(!this.classList.contains("mini-product-tile--open"));
    };

    #handleClose = (event) => {
      event.preventDefault();
      this.#closeDrawer();
    };

    #handleDialogClick = (event) => {
      if (event.target === this.#dialog) this.#closeDrawer();
    };

    #handleDialogCancel = (event) => {
      event.preventDefault();
      this.#closeDrawer();
    };

    #handleDialogClose = () => {
      this.#dialog?.classList.remove("mini-product-tile__dialog--active");
      this.#toggle?.setAttribute("aria-expanded", "false");
      document.documentElement.classList.remove(
        "mini-product-tile-drawer-open",
      );

      if (this.#toggle?.isConnected) this.#toggle.focus();
    };
  }

  customElements.define(COMPONENT_NAME, MiniProductTile);
})();
